import { PokemonType } from "prisma-layer";
import { DeckCardSlot } from "types/item-cards/deck-item-slot";
import { getItemCardDefinition } from "utils/item-cards/get-item-card-definition";
import { DECK_CARD_SLOT_COUNT } from "./deck-validation";

export interface EnergyFillResult {
  /** Energy slots to append to the deck, honoring owned/slot limits. */
  slotsToAdd: DeckCardSlot[];
  /** Per-type shortfall that couldn't be added because the player is out of that energy. */
  missingByOwned: Partial<Record<PokemonType, number>>;
  /** Requested energy that didn't fit in the remaining card slots. */
  droppedForSpace: number;
}

/**
 * Computes which energy cards to add so the deck holds enough energy for every
 * placed item card (sum of each item's per-type `energyCost`), counting energy
 * already in the deck toward that requirement. Bounded by owned energy and the
 * remaining card slots — any shortfall is reported for the caller to surface.
 */
export const computeEnergyFill = (
  cardSlots: DeckCardSlot[],
  ownedEnergy: Array<{ type: PokemonType; amount: number }>,
): EnergyFillResult => {
  const required: Partial<Record<PokemonType, number>> = {};
  const present: Partial<Record<PokemonType, number>> = {};

  for (const slot of cardSlots) {
    if (slot.kind === "energy") {
      present[slot.energy_type] = (present[slot.energy_type] ?? 0) + 1;
      continue;
    }
    const def = getItemCardDefinition(slot.item_card_key);
    if (!def) continue;
    for (const [type, cost] of Object.entries(def.energyCost) as Array<[PokemonType, number]>) {
      required[type] = (required[type] ?? 0) + cost;
    }
  }

  const ownedByType = new Map<PokemonType, number>();
  for (const energy of ownedEnergy) ownedByType.set(energy.type, energy.amount);

  let freeSlots = DECK_CARD_SLOT_COUNT - cardSlots.length;
  const slotsToAdd: DeckCardSlot[] = [];
  const missingByOwned: Partial<Record<PokemonType, number>> = {};
  let droppedForSpace = 0;

  for (const [type, need] of Object.entries(required) as Array<[PokemonType, number]>) {
    const shortfall = Math.max(0, need - (present[type] ?? 0));
    if (shortfall === 0) continue;

    const ownedAvailable = ownedByType.get(type) ?? 0;
    const ownedRemaining = Math.max(0, ownedAvailable - (present[type] ?? 0));
    const missingOwned = shortfall - ownedRemaining;
    if (missingOwned > 0) missingByOwned[type] = missingOwned;

    const addable = Math.min(shortfall, ownedRemaining);
    const fitting = Math.min(addable, freeSlots);
    droppedForSpace += addable - fitting;
    freeSlots -= fitting;

    for (let i = 0; i < fitting; i++) {
      slotsToAdd.push({ kind: "energy", energy_type: type });
    }
  }

  return { slotsToAdd, missingByOwned, droppedForSpace };
};
